/**
 * <StudentHomePage> — l'accueil de l'élève, en « arène » : son salut, son prochain live en temps
 * fort, la mission de Sam, puis son jeu (page).
 *
 * La forme est celle de l'accueil projeté par le livre (chap. 17) ; ce qui s'y trouve est ce que
 * l'app a aujourd'hui (`TodoListScreen.js`, branche élève). Ses activités n'y sont pas : elles ont
 * leur onglet, « Activités » (`<ActivitiesPage voice="student">`), et l'accueil ne les redit pas,
 * même la prochaine (Wajih, 17/10 : la mission de Sam le dit déjà). Dans l'ordre :
 *  1. le salut, l'anneau de son prochain titre en face, ses compteurs dessous
 *     (`<StudentHomeGreeting>`) ;
 *  2. le bandeau d'essai, s'il a encore à se présenter avant sa séance d'essai : une ligne
 *     « à faire » (`<TodoRow>`), avec sa croix ;
 *  3. son prochain live, SEUL dans le cadre des temps forts. La carte ne parle que du live : la
 *     date et l'heure, le tuteur, le décompte, et le jour même « GO ! ». Pas de phrase en plus
 *     (Wajih, 17/10 : « Camille t'attend à 18h00. » redisait la date et l'heure du haut de la
 *     carte). Le live nomme SON tuteur ;
 *  4. la mission de Sam (`samMission`) : SA carte, juste sous celle du live, sans cadre, sur
 *     l'aurore, Sam en grand, combien d'activités et combien sont faites, « Je lance la mission »
 *     puis « Je continue » (`<SamMissionCard>`). Elle existe un jour sans live, avec un prochain
 *     live, Sam ouvert pour l'élève et au moins une activité à faire. Sans mission, la même carte
 *     dit « Étudie avec Sam en attendant » (`kind: "study"`, l'ancien bandeau, Wajih 19/10). Ces
 *     jours-là, c'est elle qui agit : la carte du live n'a pas de bouton. Sans elle (un jour de live, ou un élève sans Sam :
 *     un essai, Sam éteint), la carte du live garde son « GO ! » secondaire des autres jours, qui
 *     explique que le live n'ouvre que le jour même ;
 *  5. les lives qui suivent le prochain, deux au plus en lignes courtes, puis « Tous mes lives »
 *     (`<StudentHomeFollowingLives>`, Wajih, 18/10) : l'élève voit ses prochains lives sans aller
 *     dans « Messages » ;
 *  6. son prochain contrôle, à la place qu'il a aujourd'hui (`assessmentSlot`, une fente : le
 *     widget appartient aux contrôles) ;
 *  7. son jeu : la quête où il en est, puis son rang (`<StudentGameSummary>`).
 * À partir de 800 px, le contrôle d'un côté, le jeu de l'autre (`<StudentHomeColumns>`), comme
 * « Aujourd'hui » et la carte de défi du livre ; l'ordre des blocs, lui, ne change pas.
 *
 * Ce qui manque ne prend pas de place : sans prochain live, ni temps fort ni mission (l'ancien
 * bandeau ne rendait rien non plus) ; sans jeu, ni anneau, ni compteurs, ni résumé.
 *
 * Trois places gardées, vides aujourd'hui, pour ce que le livre montre et que l'app n'a pas : la
 * série en bout de ligne des compteurs (`streak`), le défi de la semaine sous la quête
 * (`challenge`), la ligue sous le rang (`league`). Elles se remplissent sans redessiner la page.
 *
 * « GO ! » repart à l'écran (`onJoinSession`) : c'est lui qui fait entrer, ou qui ouvre la boîte
 * qui explique (`<LiveAccessDialog>`).
 *
 * Hors de cette page : le live et son pre-call, l'exécution d'une activité (avec ou sans Sam), la
 * liste des activités (son onglet), les écrans du jeu (les quêtes, le classement), et l'en-tête de
 * la coquille, qui n'a pas de compteurs sur cet onglet : la page les dit sous le salut, une seule
 * ligne.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { CardList } from "../../molecules/CardList"
import { SamMissionCard, type UISamMission } from "../../molecules/SamMissionCard"
import { TodoRow, type UITodoRowItem } from "../../molecules/TodoRow"
import { QuestChampion, type UIQuest } from "../../organisms/QuestCard"
import { StudentGameSummary } from "../../organisms/StudentGameSummary"
import { NextSessionSection } from "../../templates/NextSessionSection"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { StudentHomeColumns } from "./StudentHomeColumns"
import { StudentHomeFollowingLives } from "./StudentHomeFollowingLives"
import { StudentHomeGreeting, type StudentHomeCounters, type StudentHomeTitleProgress } from "./StudentHomeGreeting"
import { StudentHomeSkeleton } from "./StudentHomeSkeleton"

export type StudentHomePageProps = {
    // ── Le salut ──
    /** Le prénom de l'élève : « Salut Damien ». Absent : pas de tête, la page s'ouvre sur la suite. */
    firstname?: string
    /** Ses pétales, son XP, son titre : la ligne sous le salut. Absent : pas de ligne. */
    counters?: StudentHomeCounters | null
    /** Son titre et le chemin vers le suivant : l'anneau en face du salut. Absent : pas d'anneau. */
    titleProgress?: StudentHomeTitleProgress | null
    /** Place gardée : la série, en bout de ligne des compteurs. Rien aujourd'hui. */
    streak?: React.ReactNode

    // ── Le bandeau d'essai ──
    /** La tâche de l'élève en essai (compléter son profil). `null` : pas de bandeau. */
    trialTodo?: UITodoRowItem | null
    onTrialTodoPress?: (todo: UITodoRowItem) => void
    /** La croix du bandeau : il se masque jusqu'au prochain passage. Absent : pas de croix. */
    onDismissTrialTodo?: () => void

    // ── Le prochain live ──
    /** Le prochain live — `null` : la section n'est pas montée. */
    nextSession?: UILiveSession | null
    /**
     * « GO ! » — il presse TOUJOURS. C'est l'écran qui fait entrer (le jour du live), ou qui ouvre
     * la boîte qui explique (`<LiveAccessDialog>`).
     */
    onJoinSession?: (session: UILiveSession) => void
    /** Les lives après le prochain, en lignes courtes sous sa carte. Vide par défaut : pas de ligne. */
    followingLives?: UILiveSession[]
    /** Une ligne d'un live suivant touchée : l'écran répond pour SA séance, comme « GO ! ». */
    onSelectFollowingLive?: (session: UILiveSession) => void
    /** « Tous mes lives », sous les lignes. Absent : pas de lien. */
    onOpenAllLives?: () => void

    // ── La mission de Sam ──
    /**
     * Ses activités avant le prochain live, et combien sont faites : sa carte, sous le live. Le
     * view-model ne la donne qu'un jour sans live, Sam ouvert pour cet élève ; elle ne se rend
     * qu'avec un prochain live et au moins une activité. `null` : pas de carte.
     */
    samMission?: UISamMission | null
    /** « Je lance la mission », « Je continue », « Je commence » : l'écran répond (Sam, ou la boîte du paiement). */
    onStartSamMission?: () => void

    // ── Le prochain contrôle ──
    /** Le widget « Prochain contrôle », construit par les contrôles et branché ici. */
    assessmentSlot?: React.ReactNode

    // ── Le jeu ──
    /** La quête où l'élève en est. `null` : pas de carte. */
    quest?: UIQuest | null
    /** Ouvre « Mes quêtes ». */
    onOpenQuests?: () => void
    /** Son rang au classement général, « 12ᵉ ». Absent : il n'est pas encore classé, pas de ligne. */
    rankLabel?: string | null
    /** Ouvre le classement. */
    onOpenRanking?: () => void
    /** Place gardée : le défi de la semaine, sous la quête. Rien aujourd'hui. */
    challenge?: React.ReactNode
    /** Place gardée : la ligue, sous le rang. Rien aujourd'hui. */
    league?: React.ReactNode

    // ── État de page ──
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function StudentHomePage(props: StudentHomePageProps) {
    const {
        firstname,
        counters,
        titleProgress,
        streak,
        trialTodo,
        onTrialTodoPress,
        onDismissTrialTodo,
        nextSession = null,
        onJoinSession,
        followingLives = [],
        onSelectFollowingLive,
        onOpenAllLives,
        samMission,
        onStartSamMission,
        assessmentSlot,
        quest,
        onOpenQuests,
        rankLabel,
        onOpenRanking,
        challenge,
        league,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    const hasRank = rankLabel != null && rankLabel !== ""
    const hasGame = quest != null || hasRank || challenge != null || league != null
    const mission =
        nextSession != null && samMission != null && (samMission.kind === "study" || samMission.total > 0)
            ? samMission
            : null

    return (
        <PageTemplate
            testID={testID}
            loading={loading}
            skeleton={<StudentHomeSkeleton firstname={firstname} />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            <Stack gap="6">
                {firstname != null && firstname !== "" && (
                    <StudentHomeGreeting
                        firstname={firstname}
                        counters={counters}
                        titleProgress={titleProgress}
                        streak={streak}
                    />
                )}

                {trialTodo != null && (
                    <CardList>
                        <TodoRow todo={trialTodo} onPress={onTrialTodoPress} onDismiss={onDismissTrialTodo} />
                    </CardList>
                )}

                {nextSession != null && (
                    <Stack gap="4">
                        <NextSessionSection
                            session={nextSession}
                            onJoinSession={onJoinSession}
                            // Quand la mission de Sam agit, « GO ! » n'est plus que le mot du jour du live.
                            joinDisplay={mission != null ? "today" : "always"}
                            voice="student"
                        />
                        {mission != null && (
                            <SamMissionCard
                                done={mission.done}
                                total={mission.total}
                                kind={mission.kind}
                                tutor={nextSession.tutor}
                                onStart={() => onStartSamMission?.()}
                            />
                        )}
                    </Stack>
                )}

                {nextSession != null && (
                    <StudentHomeFollowingLives
                        sessions={followingLives}
                        onSelectSession={onSelectFollowingLive}
                        onOpenAllLives={onOpenAllLives}
                    />
                )}

                <StudentHomeColumns
                    main={assessmentSlot}
                    game={
                        hasGame ? (
                            <StudentGameSummary
                                quest={quest}
                                champion={quest != null ? <QuestChampion name={quest.champion} /> : undefined}
                                onOpenQuests={onOpenQuests}
                                rankLabel={rankLabel}
                                onOpenRanking={onOpenRanking}
                                challenge={challenge}
                                league={league}
                            />
                        ) : undefined
                    }
                />
            </Stack>
        </PageTemplate>
    )
}
